import { useEffect, useMemo, useState } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { ArrowLeftIcon, BlocksIcon, PlayIcon, PlusIcon } from 'lucide-react';
import { FieldErrors, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderDeskDropdown,
  DeskDropdown,
} from '@/components/organisms/PageSubHeader';
import {
  isEgressSipAuthPairValid,
  normalizeCartesiaPronunciationDictionary,
  normalizeSttKeywords,
  repackElevenLabsPronunciationDictionary,
} from '@/components/pages/Integration/schemas';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ADVANCED_VOICE_FIELDS } from '@/constants/voicePresets';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { cn } from '@/lib/utils';
import { useRules } from '@/modules/rules/hooks/useRules';
import { hasAssignedAiAgent } from '@/modules/rules/util';
import { selectPreviewWebchatUrl } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug, selectIntegrationId } from '@/redux/session/selectors';
import { EmailConfig, IntegrationType } from '@/shared/types/integration';
import { actions as permissionActions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getChannelKey } from '@/util/channelName';
import { CHANNELS_WS_URL, isApiKeyMasked } from '@/util/constants';
import { noop, removeEmptyFieldsDeep } from '@/util/util';

// Per-integration keys whose empty string must reach orch unchanged.
// removeEmptyFieldsDeep would otherwise drop them, hiding the operator's
// intent to clear the field from the PATCH body.
const CONFIG_KEYS_TO_SKIP: Record<string, string[]> = {
  [IntegrationType.TWILIO_VOICE]: ['voice_psid_field'],
  [IntegrationType.TELNYX_VOICE]: [
    'egress_sip_address',
    'egress_sip_auth_username',
    'egress_sip_auth_password',
    'voice_psid_field',
  ],
};

// Once we migrate every integration to use the new form, we can remove this list
const INTEGRATION_TYPES_WITH_FORM: IntegrationType[] = [
  IntegrationType.TWILIO_VOICE,
  IntegrationType.TELNYX_VOICE,
  IntegrationType.WEB_VOICE,
];

// Below 1024px: keep only the last breadcrumb level and collapse the preview button to an icon.
const HIDE_BELOW_LG = 'max-lg:hidden';
const PREVIEW_BUTTON_COLLAPSE = 'max-lg:w-9 max-lg:min-w-0 max-lg:px-0';

// The three voice integrations share one preview button; only the click handler differs.
// It collapses to an icon below 1024px, so an InfoTooltip keeps the "Preview" label reachable.
const VoicePreviewButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <InfoTooltip content={t('common.preview')}>
      <Button
        size="sm"
        variant="outline-neutral"
        onClick={onClick}
        aria-label={t('common.preview')}
        className={PREVIEW_BUTTON_COLLAPSE}
      >
        <PlayIcon />
        <span className={cn('px-1', HIDE_BELOW_LG)}>{t('common.preview')}</span>
      </Button>
    </InfoTooltip>
  );
};

// A validation error on a collapsed Advanced Settings field is invisible, so we toast.
// `language` is excluded — it lives in the always-visible Configuration card.
// voice_psid_field is not in ADVANCED_VOICE_FIELDS because a preset must not own it.
const ADVANCED_ERROR_FIELDS = [...ADVANCED_VOICE_FIELDS, 'voice_psid_field'];

const hasAdvancedVoiceError = (errors: FieldErrors): boolean => {
  const configErrors = errors.config;
  if (!configErrors || typeof configErrors !== 'object') {
    return false;
  }
  return Object.keys(configErrors).some((key) =>
    ADVANCED_ERROR_FIELDS.some((prefix) => key.startsWith(prefix))
  );
};

const IntegrationsHeader = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const deskId = useDeskId();
  const { desk } = useDesks(deskId);
  const { rules, listStatus: rulesStatus } = useRules(deskId);
  const integrationId = useSelector(selectIntegrationId);
  const slug = useSelector(selectAccountSlug);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', permissionActions.WRITE)
  );
  const { integration, isVoiceIntegration, updateIntegration, updateStatus } = useIntegrations(
    deskId,
    integrationId
  );
  const form = useFormContext();
  // This header also renders outside a form, so useFormState (which needs one) is not an
  // option. Subscribe instead: reading form.formState here would go stale once compiled.
  const subscribeToForm = form?.subscribe;
  // subscribe() only fires on change, so seed from the current state
  const [isFormDirty, setIsFormDirty] = useState(() => !isEmpty(form?.formState.dirtyFields));
  useEffect(
    () =>
      subscribeToForm?.({
        formState: { dirtyFields: true },
        callback: ({ dirtyFields }) => setIsFormDirty(!isEmpty(dirtyFields)),
      }),
    [subscribeToForm]
  );

  // Check if we're on the emails page or draft page
  const isEmailsPage = location.pathname.endsWith('/email');
  const isEmailDraftPage = location.pathname.endsWith('/email/draft');

  const webChatIntegrationId = useMemo(
    () => (integration?.type === 'web' ? integration?.integration_id : null),
    [integration]
  );
  const previewWidgetUrl = useSelector((state: RootState) =>
    selectPreviewWebchatUrl(state, webChatIntegrationId)
  );

  const handlePreviewWebChatClick = () => {
    // The preview page can't reach our APIs, so a webchat with no AI agent
    // assigned in Rules opens to a silent, unresponsive chat. Warn (and link to
    // Rules) instead of opening it. Only gate once rules have loaded, otherwise
    // fall back to opening the preview to avoid a false "no agent" warning.
    if (rulesStatus === 'success' && !hasAssignedAiAgent(rules.rules ?? [])) {
      toast.warning(t('integrations.preview_no_agent_title'), {
        description: t('integrations.preview_no_agent_description'),
        action: {
          label: t('common.assign'),
          onClick: () => navigate(`/${slug}/environments/${deskId}/rules`),
        },
      });
      return;
    }
    window.open(previewWidgetUrl, '_blank');
  };

  const handlePreviewVoiceClick = () => {
    const url = `https://web-client.moveo.ai/preview-audio?host=${encodeURIComponent(CHANNELS_WS_URL)}&integrationId=${integration?.integration_id}`;
    window.open(url, '_blank');
  };

  const handlePreviewWebVoiceClick = () => {
    const url = `https://web-client.moveo.ai/preview-webrtc?host=${encodeURIComponent(CHANNELS_WS_URL)}&integrationId=${integration?.integration_id}`;
    window.open(url, '_blank');
  };

  const handlePreviewTelnyxVoiceClick = () => {
    const url = `https://web-client.moveo.ai/preview-telnyx?host=${encodeURIComponent(CHANNELS_WS_URL)}&integrationId=${integration?.integration_id}`;
    window.open(url, '_blank');
  };

  const handleOnSubmit = () => {
    if (!form) {
      return;
    }
    const formValues = form.getValues();

    // BYOC egress SIP digest auth is both-or-neither, and only applies to a
    // verified caller ID (the fields are hidden for native numbers, which clear
    // them). Enforce at save: the form validates onChange, so a per-field yup
    // rule would flag it on the first keystroke.
    if (
      integration?.type === IntegrationType.TELNYX_VOICE &&
      formValues.config?.phone_number_kind === 'verified' &&
      !isEgressSipAuthPairValid(
        formValues.config?.egress_sip_auth_username,
        formValues.config?.egress_sip_auth_password
      )
    ) {
      form.setError(
        formValues.config?.egress_sip_auth_username
          ? 'config.egress_sip_auth_password'
          : 'config.egress_sip_auth_username',
        {
          type: 'manual',
          message: t('integrations.telnyx_voice_connect.egress_sip_auth.both_or_neither_error'),
        }
      );
      return;
    }

    const cleanedFormValues = removeEmptyFieldsDeep(
      formValues,
      // Some values allow empty strings, so we skip them from being removed
      CONFIG_KEYS_TO_SKIP[integration?.type || ''] || []
    );

    // Extract active and config from form values
    const { active, config, ...otherValues } = cleanedFormValues;

    const finalConfig = { ...integration?.config, ...config };

    if (isVoiceIntegration) {
      // Clearing phone_number must not be shadowed by the previously saved value.
      // Drop phone_number_kind alongside since it describes a number that no longer
      // exists. phone_number_id is IC-managed; leave its lifecycle to IC.
      if (!formValues.config?.phone_number) {
        delete finalConfig.phone_number;
        delete finalConfig.phone_number_kind;
      }
      // Verified caller IDs are not Telnyx-owned; the orchestrator schema forbids
      // phone_number_id when kind=verified. Clear defensively in case the spread
      // carried a stale value from a prior native pick.
      if (formValues.config?.phone_number_kind === 'verified') {
        delete finalConfig.phone_number_id;
      }
      normalizeCartesiaPronunciationDictionary(finalConfig.tts_cartesia);
      normalizeSttKeywords(finalConfig);
      repackElevenLabsPronunciationDictionary(finalConfig.tts_elevenlabs);

      // BYO ElevenLabs key: strip masked values so the backend preserves the existing key
      if (isApiKeyMasked(finalConfig.tts_elevenlabs?.api_key)) {
        delete finalConfig.tts_elevenlabs.api_key;
      }

      // Orchestrator requires model_id whenever tts_elevenlabs.language_code is set.
      // Language sync writes language_code into every provider block, so a config
      // whose ElevenLabs block has no model_id (e.g. active provider is MiniMax/
      // Cartesia) would fail validation. Drop the orphan language_code.
      if (finalConfig.tts_elevenlabs?.language_code && !finalConfig.tts_elevenlabs?.model_id) {
        delete finalConfig.tts_elevenlabs.language_code;
      }

      // Soniox: speaker_policy is derived from the Speaker Isolation toggle.
      // Orchestrator rejects speaker_policy without enable_speaker_diarization=true.
      if (finalConfig.stt_soniox) {
        if (finalConfig.stt_soniox.enable_speaker_diarization === true) {
          finalConfig.stt_soniox.speaker_policy = 'primary_only';
        } else {
          delete finalConfig.stt_soniox.speaker_policy;
        }
      }
    }

    // Twilio-specific config fixups
    if (integration?.type === IntegrationType.TWILIO_VOICE) {
      // If welcome_trigger_message was cleared (checkbox unchecked), remove it from the payload
      if (!formValues.config?.welcome_trigger_message) {
        delete finalConfig.welcome_trigger_message;
      }
    }

    updateIntegration({
      ...integration,
      active: active !== undefined ? active : integration?.active,
      config: finalConfig,
      ...otherValues,
    });
  };

  // Advanced Settings starts collapsed, so an error there is invisible. Toast to
  // point the operator at the advanced section instead of silently failing Save.
  const handleOnInvalid = (errors: FieldErrors) => {
    if (hasAdvancedVoiceError(errors)) {
      toast.error(t('integrations.voice_common.advanced.invalid_toast'));
    }
  };

  const hasUnsavedChanges =
    form &&
    integration?.type &&
    INTEGRATION_TYPES_WITH_FORM.includes(integration.type) &&
    isFormDirty;

  usePrompt(
    Boolean(hasUnsavedChanges),
    t('prompts.unsaved_changes.subtitle'),
    t('prompts.unsaved_changes.title'),
    handleOnSubmit
  );

  const actions = (
    <>
      {integration?.type === 'web' && integration?.active && (
        <Button size="sm" onClick={handlePreviewWebChatClick}>
          {t('integrations.web_preview')}
        </Button>
      )}
      {integration?.type === IntegrationType.TWILIO_VOICE && integration?.active && (
        <VoicePreviewButton onClick={handlePreviewVoiceClick} />
      )}
      {integration?.type === IntegrationType.WEB_VOICE && integration?.active && (
        <VoicePreviewButton onClick={handlePreviewWebVoiceClick} />
      )}
      {integration?.type === IntegrationType.TELNYX_VOICE && integration?.active && (
        <VoicePreviewButton onClick={handlePreviewTelnyxVoiceClick} />
      )}
      {form && INTEGRATION_TYPES_WITH_FORM.includes(integration?.type) && (
        <Button
          size="sm"
          variant="primary"
          onClick={form.handleSubmit(handleOnSubmit, handleOnInvalid)}
          className="min-w-18"
          disabled={!hasUnsavedChanges || !canWrite}
          isLoading={updateStatus === 'pending'}
        >
          {t('common.save')}
        </Button>
      )}
      {isEmailsPage && canWrite && (
        <Link to={`/${slug}/environments/${deskId}/integrations/email/draft`}>
          <Button size="sm" onClick={noop}>
            <PlusIcon />
            {t('email.add_email')}
          </Button>
        </Link>
      )}
    </>
  );

  // Email integration detail page
  if (integrationId && integration?.type === IntegrationType.EMAIL) {
    return (
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <div className="flex items-center">
              <Link to={`/${slug}/environments/${deskId}/integrations/email`}>
                <Button variant="ghost-neutral" size="icon-sm" onClick={noop}>
                  <ArrowLeftIcon size={16} />
                </Button>
              </Link>
              <div className="mr-4 ml-2 h-4">
                <Separator orientation="vertical" />
              </div>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem className={HIDE_BELOW_LG}>
                    <BreadcrumbLink asChild>
                      <Link
                        to={`/${slug}/environments/${deskId}/integrations`}
                        className="breadcrumb-link"
                      >
                        {t('common.connections')}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className={HIDE_BELOW_LG} />
                  <BreadcrumbItem className={HIDE_BELOW_LG}>
                    <BreadcrumbLink asChild>
                      <Link
                        to={`/${slug}/environments/${deskId}/integrations/email`}
                        className="breadcrumb-link"
                      >
                        {t('email.title')}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className={HIDE_BELOW_LG} />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="gap-4">
                      {(integration?.config as EmailConfig)?.from_email || t('email.new_email')}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </PageSubHeaderBreadcrumbs>
        }
        actions={actions ? <PageSubHeaderActions>{actions}</PageSubHeaderActions> : undefined}
      />
    );
  }

  // Other integration detail page
  if (integrationId) {
    return (
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <div className="flex items-center">
              <Link to={`/${slug}/environments/${deskId}/integrations`}>
                <Button variant="ghost-neutral" size="icon-sm" onClick={noop}>
                  <ArrowLeftIcon size={16} />
                </Button>
              </Link>
              <div className="mr-4 ml-2 h-4">
                <Separator orientation="vertical" />
              </div>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem className={HIDE_BELOW_LG}>
                    <BreadcrumbLink asChild>
                      <Link
                        to={`/${slug}/environments/${deskId}/integrations`}
                        className="breadcrumb-link"
                      >
                        {t('common.connections')}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className={HIDE_BELOW_LG} />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="gap-4">
                      {integration?.type ? (
                        t(getChannelKey(integration?.type))
                      ) : (
                        <Skeleton className="h-4 w-16" />
                      )}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                  <Badge
                    flavour="neutral"
                    type="outline"
                    className="h-6 text-xs font-semibold select-none"
                    rounded={false}
                  >
                    {desk?.name}
                  </Badge>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </PageSubHeaderBreadcrumbs>
        }
        actions={actions ? <PageSubHeaderActions>{actions}</PageSubHeaderActions> : undefined}
      />
    );
  }

  // Email draft page - show "Connections > Your emails > New email" breadcrumb
  if (isEmailDraftPage) {
    return (
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <div className="flex items-center">
              <Link to={`/${slug}/environments/${deskId}/integrations/email`}>
                <Button variant="ghost-neutral" size="icon-sm">
                  <ArrowLeftIcon size={16} />
                </Button>
              </Link>
              <div className="mr-4 ml-2 h-4">
                <Separator orientation="vertical" />
              </div>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem className={HIDE_BELOW_LG}>
                    <BreadcrumbLink asChild>
                      <Link
                        to={`/${slug}/environments/${deskId}/integrations`}
                        className="breadcrumb-link"
                      >
                        {t('common.connections')}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className={HIDE_BELOW_LG} />
                  <BreadcrumbItem className={HIDE_BELOW_LG}>
                    <BreadcrumbLink asChild>
                      <Link
                        to={`/${slug}/environments/${deskId}/integrations/email`}
                        className="breadcrumb-link"
                      >
                        {t('email.title')}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className={HIDE_BELOW_LG} />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="gap-4">{t('email.new_email')}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </PageSubHeaderBreadcrumbs>
        }
      />
    );
  }

  // Emails page - show "Connections > Your emails" breadcrumb
  if (isEmailsPage) {
    return (
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <div className="flex items-center">
              <Link to={`/${slug}/environments/${deskId}/integrations`}>
                <Button variant="ghost-neutral" size="icon-sm" onClick={noop}>
                  <ArrowLeftIcon size={16} />
                </Button>
              </Link>
              <div className="mr-4 ml-2 h-4">
                <Separator orientation="vertical" />
              </div>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem className={HIDE_BELOW_LG}>
                    <BreadcrumbLink asChild>
                      <Link
                        to={`/${slug}/environments/${deskId}/integrations`}
                        className="breadcrumb-link"
                      >
                        {t('common.connections')}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className={HIDE_BELOW_LG} />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="gap-4">{t('email.title')}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </PageSubHeaderBreadcrumbs>
        }
        actions={actions ? <PageSubHeaderActions>{actions}</PageSubHeaderActions> : undefined}
        deskDropdown={
          <PageSubHeaderDeskDropdown>
            <DeskDropdown getPath={(id) => `/${slug}/environments/${id}/integrations/email`} />
          </PageSubHeaderDeskDropdown>
        }
      />
    );
  }

  // Integrations list page
  return (
    <PageSubHeader
      breadcrumbs={
        <PageSubHeaderBreadcrumbs>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <div className="flex h-8 w-8 items-center justify-center">
                    <BlocksIcon size={16} className="text-muted-foreground" />
                  </div>
                  <div className="mr-4 ml-2 h-4">
                    <Separator orientation="vertical" />
                  </div>
                  {t('common.connections')}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </PageSubHeaderBreadcrumbs>
      }
      deskDropdown={
        <PageSubHeaderDeskDropdown>
          <DeskDropdown getPath={(id) => `/${slug}/environments/${id}/integrations`} />
        </PageSubHeaderDeskDropdown>
      }
    />
  );
};

export default IntegrationsHeader;
